import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import logo1 from "../../assets/Images/logo1.png";
import logo2 from "../../assets/Images/logo2.png";

const schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      setServerError("");
      setIsLoading(true);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIsSubmitted(true);
    } catch (error) {
      setServerError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src={logo1}
              onClick={() => navigate("/")}
              alt="Logo for Light Mode"
              className="block dark:hidden h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            />
            <img
              src={logo2}
              onClick={() => navigate("/")}
              alt="Logo for Dark Mode"
              className="hidden dark:block h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity"
            />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            Forgot your password?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            {isSubmitted 
              ? "We've sent a password reset link to your email" 
              : "Enter your email to reset your password"}
          </p>
        </div>

        {serverError && (
          <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 rounded-lg text-sm">
            {serverError}
          </div>
        )}

        {isSubmitted ? (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <FontAwesomeIcon 
                icon={faEnvelope} 
                className="h-5 w-5 text-green-600 dark:text-green-400" 
              />
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-6">
              Check your email for a link to reset your password. If it doesn't appear within a few minutes, check your spam folder.
            </p>
            <button
              onClick={() => navigate("/log-in")}
              className="w-full bg-[#74D19C] hover:bg-[#74D19C]/90 text-white font-medium py-2.5 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-[#74D19C] focus:ring-offset-2"
            >
              Return to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Email Address
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <input
                      {...field}
                      type="email"
                      id="email"
                      placeholder="your@email.com"
                      className={`w-full px-4 py-2 border ${
                        errors.email
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-indigo-500"
                      } rounded-lg focus:outline-none focus:ring-1 transition bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                      disabled={isLoading}
                    />
                  </div>
                )}
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#74D19C] hover:bg-[#74D19C]/90 text-white font-medium py-2.5 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-[#74D19C] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faCircleNotch}
                    className="animate-spin mr-2"
                  />
                  Sending...
                </span>
              ) : (
                "Send reset link"
              )}
            </button>

            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              Remember your password?{" "}
              <button
                type="button"
                className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 font-medium focus:outline-none"
                onClick={() => navigate("/log-in")}
                disabled={isLoading}
              >
                Sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;